import { ActionIcon, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/table-core';
import camelCase from 'lodash-es/camelCase';
import { getLogger } from 'loglevel';
import { type ReactNode, useCallback, useMemo } from 'react';

import {
	ActivityLogIcon,
	ArchiveIcon,
	ClearIcon,
	Download,
	ErrorIcon,
	ExportIcon,
	LockedLineIcon,
	Success,
	Upload,
} from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import type { DataTableFeatures } from '@apple/ui/data-table/features';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { Link } from '@apple/ui/link/Link';
import { ServerValidationError } from '@apple/utils/api/errors';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import type { Guid } from '@apple/utils/primitive';

import { useAuthContext } from '@apple/features/auth/hooks/useAuthContext';
import type { Plant } from '@apple/features/plants/models/models';

import { getEventItemsDownloadUrl, getExportItemsUrl } from '../api/item-upload';
import { archiveStoreEvent, cancelStoreEvent } from '../api/management';
import { StoreEventStatusText } from '../components/event-status';
import { StoreEventTypeText } from '../components/event-type';
import { StoreEventAuditLog } from '../components/StoreEventAuditLog';
import type { StoreEventFilters, StoreEventListingModel } from '../models/listing';
import { storeEventQueryKeys } from '../queries';

const log = getLogger('store-events');

const columnHelper = createDataTableColumnHelper<StoreEventListingModel, StoreEventFilters>();

export function useStoreEventColumns({
	plants,
	onNavigateToLockedItems,
	renderShipmentReportLink,
	onUploadItems,
	uploadingItemsDisabled,
}: {
	plants: Plant[];
	onNavigateToLockedItems: (storeEventId: Guid) => Promise<void> | void;
	renderShipmentReportLink: (storeEventId: Guid) => ReactNode;
	onUploadItems: (storeEventId: Guid) => void;
	uploadingItemsDisabled: boolean;
}) {
	const { t } = useTranslation('store-event');
	const auth = useAuthContext();
	const queryClient = useQueryClient();

	const itemsTemplate = useFileDownload({
		method: 'get',
		url: getEventItemsDownloadUrl(''),
		fallbackFilename: 'event_items.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});

	const handleDownloadItems = useCallback(
		(id: string) => {
			void itemsTemplate.download({ url: getEventItemsDownloadUrl(id) });
		},
		[itemsTemplate],
	);

	const handleExportProducts = useCallback(
		(id: string) => {
			void itemsTemplate.download({ url: getExportItemsUrl(id) });
		},
		[itemsTemplate],
	);

	const handleShowLog = useCallback((storeEventId: Guid) => {
		modals.open({
			children: <StoreEventAuditLog storeEventId={storeEventId} />,
			size: '95%',
		});
	}, []);

	const cancelStoreEventMutation = useMutation({
		mutationFn: cancelStoreEvent,
		onSuccess: async () => {
			await handleSuccessfulChange(t('storeEvents.cancelModal.success'));
		},
		onError: showErrorNotification,
	});

	const archiveStoreEventMutation = useMutation({
		mutationFn: archiveStoreEvent,
		onSuccess: async () => {
			await handleSuccessfulChange(t('storeEvents.archiveModal.success'));
		},
		onError: showErrorNotification,
	});

	const handleArchiveClick = useCallback(
		(storeEventId: string) => {
			if (!archiveStoreEventMutation) {
				return;
			}
			modals.openConfirmModal({
				title: t('storeEvents.archiveModal.title'),
				children: <Text size='sm'>{t('storeEvents.archiveModal.message')}</Text>,
				labels: {
					confirm: t('common:buttons.confirm'),
					cancel: t('common:buttons.cancel'),
				},
				onConfirm: () => {
					archiveStoreEventMutation.mutate(storeEventId);
				},
			});
		},
		[archiveStoreEventMutation, t],
	);

	const handleCancelClick = useCallback(
		(storeEventId: string) => {
			if (!cancelStoreEventMutation) {
				return;
			}
			modals.openConfirmModal({
				title: t('storeEvents.cancelModal.title'),
				children: <Text size='sm'>{t('storeEvents.cancelModal.message')}</Text>,
				labels: {
					confirm: t('common:buttons.confirm'),
					cancel: t('common:buttons.cancel'),
				},
				onConfirm: () => {
					cancelStoreEventMutation.mutate(storeEventId);
				},
			});
		},
		[cancelStoreEventMutation, t],
	);

	async function handleSuccessfulChange(message?: string) {
		await queryClient.invalidateQueries({
			queryKey: storeEventQueryKeys.all,
		});

		message &&
			notifications.show({
				message: message,
				icon: <Success />,
				color: 'green',
				autoClose: 5000,
			});
	}

	function showErrorNotification(error: Error) {
		if (!(error instanceof ServerValidationError)) {
			log.error(error);
			notifications.show({
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
				color: 'red',
				autoClose: 5000,
			});
		}
	}

	const columns = useMemo(
		() =>
			[
				columnHelper.accessor('storeName', {
					enableColumnFilter: false,
					enableSorting: false,
					header: t('storeEvents.fields.storeName'),
					size: 50,
					filter: {
						group: t('storeEvents.filterGroups.details'),
						variant: 'select',
					},
					cell: ({ row }) =>
						row.original.isSubmitting ? (
							<Tooltip label={t('labels.eventNotEditable')}>
								<Text size='sm'>{row.original.storeName}</Text>
							</Tooltip>
						) : row.original.status === 'Canceled' ? (
							<Tooltip label={t('labels.eventCancelled')}>
								<Text size='sm'>{row.original.storeName}</Text>
							</Tooltip>
						) : (
							<Link
								to='/store-events/$storeEventId'
								params={{ storeEventId: row.original.id }}
							>
								<Text size='sm'>{row.original.storeName}</Text>
							</Link>
						),
				}),
				columnHelper.accessor('thirdPartyCustomerId', {
					enableColumnFilter: true,
					enableSorting: false,
					header: t('storeEvents.fields.storeId'),
					size: 50,
					filter: {
						group: t('storeEvents.filterGroups.details'),
						variant: 'text',
					},
				}),
				columnHelper.filterOnly('warehouseCode', {
					group: t('storeEvents.filterGroups.details'),
					label: t('storeEvents.filters.plant'),
					variant: 'select',
					getFilterDisplayValue: value =>
						plants.find(x => x.warehouseCode === value)?.name ?? String(value),
				}),
				columnHelper.accessor('type', {
					enableSorting: false,
					header: t('storeEvents.fields.type'),
					size: 50,
					cell: ({ row }) =>
						row.original.type && <StoreEventTypeText value={row.original.type} />,
					enableColumnFilter: true,
					filter: {
						group: t('storeEvents.filterGroups.details'),
						variant: 'select',
						getFilterDisplayValue: value =>
							t(`eventType.${camelCase(value as string)}`),
					},
				}),
				columnHelper.accessor('status', {
					enableSorting: false,
					header: t('storeEvents.fields.status'),
					size: 50,
					cell: ({ row }) => <StoreEventStatusText value={row.original.status} />,
					enableColumnFilter: true,
					filter: {
						group: t('storeEvents.filterGroups.details'),
						variant: 'select',
						getFilterDisplayValue: value =>
							t(`eventStatus.${camelCase(value as string)}`),
					},
				}),
				columnHelper.accessor('updatedDate', {
					enableSorting: false,
					header: t('storeEvents.fields.lastEditDate'),
					size: 30,
					enableColumnFilter: false,
				}),
				columnHelper.accessor('fullKitShipDate', {
					enableSorting: false,
					header: t('storeEvents.fields.shipDate'),
					size: 30,
					enableColumnFilter: false,
				}),
				columnHelper.accessor('fullKitDeliveryDate', {
					enableSorting: false,
					header: t('storeEvents.fields.offloadDate'),
					size: 30,
					enableColumnFilter: false,
				}),
				columnHelper.display({
					enableSorting: false,
					header: t('storeEvents.fields.action'),
					size: 20,
					cell: ({ row }) => {
						const hasWritePermission = auth.hasPermission(
							'AppleRetail.Security.Features.StoreEventManagement.Write',
						);
						return (
							<ActionIcon.Group>
								{hasWritePermission && row.original.canBeArchived && (
									<ArchiveIcon
										tooltip={t('storeEvents.actions.archive')}
										c='primary'
										onClick={() => handleArchiveClick(row.original.id)}
									/>
								)}
								{hasWritePermission && row.original.canItemsBeAdded && (
									<>
										<Download
											tooltip={t('storeEvents.actions.download')}
											c='primary'
											onClick={() => handleDownloadItems(row.original.id)}
											disabled={itemsTemplate.downloading}
										/>

										<Upload
											tooltip={t('storeEvents.actions.upload')}
											c='primary'
											onClick={() => onUploadItems(row.original.id)}
											data-testid={`upload-store-event-${row.original.id}`}
											disabled={uploadingItemsDisabled}
										/>
									</>
								)}
								{row.original.hasLongPendingLockedItems && (
									<LockedLineIcon
										tooltip={t('storeEvents.actions.locked')}
										c='primary'
										onClick={() =>
											void onNavigateToLockedItems(row.original.id)
										}
									/>
								)}
								{hasWritePermission && row.original.canBeCancelled && (
									<ClearIcon
										tooltip={t('storeEvents.actions.cancel')}
										c='red'
										onClick={() => handleCancelClick(row.original.id)}
										data-testid={`cancel-store-event-${row.original.id}`}
									/>
								)}
								{row.original.hasAuditLogEntries && (
									<ActivityLogIcon
										tooltip={t('storeEvents.actions.auditLog')}
										c='primary'
										onClick={() => handleShowLog(row.original.id)}
									/>
								)}
								{row.original.hasShipmentReport &&
									renderShipmentReportLink(row.original.id)}
								<ExportIcon
									tooltip={t('storeEvents.actions.export')}
									c='primary'
									onClick={() => handleExportProducts(row.original.id)}
									disabled={itemsTemplate.downloading}
								/>
							</ActionIcon.Group>
						);
					},
					enableColumnFilter: false,
				}),
				columnHelper.filterOnly('hasLongPendingLockedItems', {
					group: t('storeEvents.filterGroups.details'),
					label: t('storeEvents.filters.hasLockedItems'),
					variant: 'switch',
				}),
			] as const as ColumnDef<DataTableFeatures, StoreEventListingModel>[],
		[
			t,
			auth.hasPermission,
			handleDownloadItems,
			handleExportProducts,
			onUploadItems,
			handleArchiveClick,
			handleCancelClick,
			handleShowLog,
			plants,
			itemsTemplate.downloading,
			uploadingItemsDisabled,
			onNavigateToLockedItems,
			renderShipmentReportLink,
		],
	);

	return {
		columns,
	};
}
